import { round } from "lodash-es";
import { SimulationDemand } from "@/models/Demand";
import { Offer } from "@/models/simulation/subtypes";
import { CalculatorOfferCustomization } from "@/types/calculator";

type CreateOfferCustomization = (offer: Offer, userInput: Nullable<SimulationDemand>) => CalculatorOfferCustomization;

const MAX_SUBTRACT_PRECISION = 4;

const subtractWithPrecision = (a: number, b: number): number => {
  const decimals = (n: number) => {
    const asString = String(n);
    if (asString.includes('e')) {
      return MAX_SUBTRACT_PRECISION;
    }
    return (asString.split('.')[1] ?? '').length;
  };
  return round(a - b, Math.min(Math.max(decimals(a), decimals(b)), MAX_SUBTRACT_PRECISION));
};

/** The precision the customization modal writes at: a finer difference between a value and its
 *  default is the engine's own rounding of the same figure, not an edit the user made. */
const CUSTOMIZATION_PRECISION = 2;

export const isSameCustomizationValue = (a: number, b: number): boolean =>
  round(a, CUSTOMIZATION_PRECISION) === round(b, CUSTOMIZATION_PRECISION);

export const useCalculatorOfferCustomization = () => {
  const createOfferCustomization: CreateOfferCustomization = (offer, userInput) => {
    // The first of two periods is a fixed rate on some offers and a margin of its own on others; only one of
    // the two keys is live per offer, and both travel in the replacement's initial value - hence the one echo
    const isFixedInterestRate: boolean = Boolean(offer.changingMargin && (offer.changingMargin.initialValueStatic ?? true));
    const isTwoPeriodMargin: boolean = Boolean(offer.changingMargin) && !isFixedInterestRate;

    const fixedInterestRateDefault: number | undefined = isFixedInterestRate ? offer.customizations?.defaultInitialValue : undefined;
    const fixedInterestRateValue: number = offer.changingMargin?.initialInterestRateSum ?? 0;

    const initialMarginDefault: number | undefined = offer.customizations?.defaultInitialValue;
    const initialMarginValue: number = isTwoPeriodMargin ? offer.changingMargin?.initialValue ?? 0 : 0;

    const marginDefault: number | undefined = offer.changingMargin
      ? offer.customizations?.defaultEventualValue
      : offer.customizations?.defaultInitialValue;
    const marginValue: number = offer.changingMargin?.eventualValue ?? offer.interestRate.margin;

    const lpsIncreasingMargin: number = offer.lowPersonalShareIncreasingMargin?.increasingMargin ?? 0;

    return {
      loanAmount: {
        current: offer.customizations?.customLoanAmount ?? userInput?.loanAmount ?? 0,
        default: userInput?.loanAmount ?? 0,
      },
      hypothecValue: {
        current: offer.customizations?.customHypothecValue ?? userInput?.hypothecValue ?? 0,
        default: userInput?.hypothecValue ?? 0,
      },
      loanPeriod: {
        current: offer.customizations?.customLoanPeriodInMonths ?? userInput?.loanPeriodInMonths ?? userInput?.loanPeriod ?? 0,
        default: userInput?.loanPeriodInMonths ?? userInput?.loanPeriod ?? 0,
      },
      fixedInterestRate: {
        current: fixedInterestRateValue,
        default: fixedInterestRateDefault ?? fixedInterestRateValue,
      },
      initialMargin: {
        current: subtractWithPrecision(initialMarginValue, typeof initialMarginDefault === 'number' ? 0 : lpsIncreasingMargin),
        default: initialMarginDefault ?? subtractWithPrecision(initialMarginValue, lpsIncreasingMargin),
      },
      margin: {
        current: subtractWithPrecision(marginValue, typeof marginDefault === 'number' ? 0 : lpsIncreasingMargin),
        default: marginDefault ?? subtractWithPrecision(marginValue, lpsIncreasingMargin),
      },
      commission: {
        current: offer.creditedCosts.includes('Provision')
          ? offer.creditedInitialCosts?.provision?.percentage ?? 0
          : offer.initialCosts.provision.percentage,
        default: offer.customizations?.defaultProvisionValue
          ?? (offer.creditedCosts.includes('Provision')
            ? offer.creditedInitialCosts?.provision?.percentage ?? 0
            : offer.initialCosts.provision.percentage),
      },
    }
  }

  return { createOfferCustomization };
}
